import PropTypes from "prop-types";
import CodeLivePreview from "../../../components/CodeLivePreview";
import SectionRenderer from "../../../components/SectionRenderer";
import { ICON_MAP } from "../../../constants/themes";

export default function PreviewPane({
	formData,
	isPreviewingCode,
	setIsPreviewingCode,
	previewCompany,
}) {
	const cat = formData.category;
	const showHeroEditor = cat === "full" || cat === "hero" || cat === "products";
	const showSectionsEditor =
		cat === "full" ||
		cat === "content" ||
		cat === "footer" ||
		cat === "products";
	const showProductsEditor = cat === "full" || cat === "products";

	const colorMap = {
		cyan: "text-cyan-400",
		purple: "text-purple-400",
		emerald: "text-emerald-400",
		blue: "text-blue-400",
		amber: "text-amber-400",
	};

	return (
		<div className="bg-[#0B0F19] rounded-xl overflow-hidden min-h-[400px]">
			{isPreviewingCode && formData.category === "full" ? (
				<div className="p-0">
					<div className="bg-slate-900/50 p-2 flex items-center justify-between border-b border-white/5">
						<span className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider ml-2">
							Sandbox Live Preview
						</span>
						<button
							type="button"
							onClick={() => setIsPreviewingCode(false)}
							className="text-[10px] px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded transition-all"
						>
							Voltar para Editor Visual
						</button>
					</div>
					<CodeLivePreview
						code={formData.template_code}
						theme={formData.theme}
						variables={formData.variables}
						codeType={formData.code_type || "react"}
					/>
				</div>
			) : (
				<>
					{/* Hero preview header */}
					<div className="bg-gradient-to-br from-[#0F1629] to-[#0B0F19] p-6 border-b border-white/5">
						<p className="text-cyan-400 text-xs font-bold uppercase tracking-wider mb-1">
							Proposta Comercial
						</p>
						<h3 className="text-lg font-bold text-white">
							{previewCompany.name}
						</h3>
						{previewCompany.tagline && (
							<p className="text-xs text-slate-400 mt-1">
								{previewCompany.tagline}
							</p>
						)}
					</div>

					{/* Hero cards preview */}
					{showHeroEditor && (formData.hero_cards || []).length > 0 && (
						<div className="p-4 border-b border-white/5">
							<div
								className={`grid gap-2 ${formData.hero_cards.length > 2 ? "grid-cols-3" : `grid-cols-${formData.hero_cards.length}`}`}
							>
								{formData.hero_cards.map((card, i) => {
									const CardIcon = ICON_MAP[card.icon] || ICON_MAP.star;
									return (
										<div
											key={i}
											className="p-3 rounded-lg bg-slate-900/80 border border-slate-800"
										>
											{CardIcon && (
												<CardIcon
													className={`w-4 h-4 mb-2 ${colorMap[card.color] || "text-cyan-400"}`}
												/>
											)}
											<p className="text-xs font-semibold text-white truncate">
												{card.title || "..."}
											</p>
											<p className="text-[10px] text-slate-500 truncate">
												{card.subtitle || "..."}
											</p>
										</div>
									);
								})}
							</div>
						</div>
					)}

					{/* Sections preview */}
					{showSectionsEditor &&
						formData.sections
							.filter(
								(s) =>
									s.type === "company_intro" ||
									s.title ||
									s.content ||
									(s.cards && s.cards.length > 0) ||
									(s.images && s.images.length > 0),
							)
							.map((section, idx) => (
								<SectionRenderer
									key={idx}
									section={section}
									index={idx}
									company={previewCompany}
									compact
								/>
							))}

					{/* Products preview */}
					{showProductsEditor && (formData.default_items || []).length > 0 && (
						<div className="p-4 border-b border-white/5">
							<h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">
								Planos & Modulos
							</h4>
							{formData.default_items.map((item, i) => (
								<div
									key={i}
									className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0"
								>
									<div className="flex items-center gap-2">
										<span
											className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
												item.item_type === "plan"
													? "bg-cyan-500/20 text-cyan-400"
													: "bg-purple-500/20 text-purple-400"
											}`}
										>
											{item.item_type === "plan" ? "P" : "M"}
										</span>
										<span className="text-xs text-white">{item.name}</span>
									</div>
									<span className="text-xs text-green-400 font-bold">
										R${" "}
										{(
											parseFloat(item.value || 0) * (item.quantity || 1)
										).toFixed(2)}
									</span>
								</div>
							))}
							<div className="mt-2 pt-2 border-t border-white/5 flex justify-between">
								<span className="text-[10px] font-semibold text-slate-400">
									Total
								</span>
								<span className="text-xs font-bold text-green-400">
									R${" "}
									{formData.default_items
										.reduce(
											(sum, i) =>
												sum + parseFloat(i.value || 0) * (i.quantity || 1),
											0,
										)
										.toFixed(2)}
								</span>
							</div>
						</div>
					)}

					{/* Empty state */}
					{!showHeroEditor && !showProductsEditor && !showSectionsEditor && (
						<div className="p-8 text-center">
							<p className="text-xs text-slate-600">Selecione uma categoria</p>
						</div>
					)}

					{showHeroEditor &&
						!(formData.hero_cards || []).length &&
						showSectionsEditor &&
						!formData.sections.length &&
						showProductsEditor &&
						!(formData.default_items || []).length && (
							<div className="p-8 text-center">
								<p className="text-xs text-slate-600">
									Adicione conteudo para ver o preview
								</p>
							</div>
						)}

					<div className="p-4 text-center border-t border-white/5">
						<p className="text-[10px] text-slate-600">
							Preview - Layout final pode variar
						</p>
					</div>
				</>
			)}
		</div>
	);
}

PreviewPane.propTypes = {
	formData: PropTypes.object.isRequired,
	isPreviewingCode: PropTypes.bool.isRequired,
	setIsPreviewingCode: PropTypes.func.isRequired,
	previewCompany: PropTypes.object.isRequired,
};
